import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, cfmt, pfmt } from '../lib/api';
import { TID } from '../constants/testIds';
import MetricBar from '../components/common/MetricBar';
import Sparkline from '../components/common/Sparkline';
import WatchlistPanel from '../components/common/WatchlistPanel';
import MapView from '../components/common/MapView';
import SEOHead from '../components/common/SEOHead';
import { useSchoolBoundaries } from '../hooks/useSchoolBoundaries';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Bookmark, ArrowUpRight, Sparkles, MessageSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function NeighbourhoodDetail() {
    const { slug } = useParams();
    const { user } = useAuth();

    const { data: n, isLoading, isError } = useQuery({
        queryKey: ['neighbourhood', slug],
        queryFn: async () => (await api.get(`/neighbourhoods/${slug}`)).data,
        retry: 1,
    });

    const insightMut = useMutation({
        mutationFn: async () => (await api.post(`/ai/insight/${slug}`)).data,
    });

    const boundaries = useSchoolBoundaries(n?.coords?.lat, n?.coords?.lng);

    const favMut = useMutation({
        mutationFn: async () => (await api.post('/user/favorites', { slug })).data,
        onSuccess: () => toast.success('Saved to your dashboard'),
        onError: () => toast.error('Sign in to save favorites'),
    });

    if (isError && !n) {
        return (
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28" data-testid="detail-not-found">
                <div className="overline">Not found</div>
                <h1 className="font-editorial text-[40px] lg:text-[56px] tracking-editorial mt-4 leading-tight">
                    We don&apos;t have a profile at this address.
                </h1>
                <p className="mt-4 text-[15px] text-neutral-600 max-w-xl">
                    The link may be out of date, or this neighbourhood isn&apos;t in our coverage yet.
                    We currently profile 35 neighbourhoods across Durham, Hamilton, Halton, Brant and Niagara.
                </p>
                <Link
                    to="/neighbourhoods"
                    className="mt-8 inline-flex items-center gap-2 bg-brand-navy text-white px-6 py-3.5 text-[13.5px] rounded-lg hover:bg-brand-gold hover:text-brand-navy transition-colors"
                    data-testid="detail-not-found-back"
                >
                    Browse all neighbourhoods <ArrowUpRight className="w-4 h-4" />
                </Link>
            </div>
        );
    }

    if (isLoading || !n) {
        return (
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 animate-pulse" aria-busy="true" aria-label="Loading neighbourhood profile">
                <div className="h-3 w-40 bg-neutral-200 rounded" />
                <div className="mt-6 h-16 w-2/3 bg-neutral-200 rounded" />
                <div className="mt-6 h-4 w-1/2 bg-neutral-100 rounded" />
                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[0, 1, 2, 3].map((k) => <div key={k} className="h-24 bg-neutral-100 rounded" />)}
                </div>
                <div className="mt-10 h-64 bg-neutral-100 rounded" />
            </div>
        );
    }

    const chartData = (n.price_history || []).map((p) => ({ year: String(p.year), price: p.price }));

    return (
        <div>
            <SEOHead
                title={`${n.name}, ${n.city} — neighbourhood profile & data`}
                description={`${n.tagline} Median price ${cfmt(n.median_price)}. Schools, safety, walkability, transit and 5-year forecast for ${n.name}, ${n.city}, Ontario.`}
                path={`/neighbourhoods/${n.slug}`}
                image={n.hero_image}
                type="article"
            />
            {/* Hero */}
            <section data-testid={TID.detail.hero} className="border-b border-neutral-200">
                <div className="grid grid-cols-1 lg:grid-cols-12">
                    <div className="lg:col-span-7 p-8 lg:p-16">
                        <div className="overline">{n.city} · {n.region ? `${n.region} · ` : ''}{n.province} · {n.postal_prefix}</div>
                        <h1 className="font-editorial text-[52px] lg:text-[80px] leading-[1] tracking-editorial mt-4">{n.name}</h1>
                        <p className="mt-6 text-[19px] text-neutral-700 max-w-xl font-editorial italic">{n.tagline}</p>

                        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-neutral-200">
                            {[
                                { l: 'Median price', v: cfmt(n.median_price) },
                                { l: '1-yr change', v: `${n.price_1yr_change_pct >= 0 ? '+' : ''}${pfmt(n.price_1yr_change_pct)}`, tone: n.price_1yr_change_pct >= 0 ? 'up' : 'down' },
                                { l: '5-yr change', v: `+${pfmt(n.price_5yr_change_pct)}` },
                                { l: '5-yr forecast', v: `+${pfmt(n.forecast_5yr_pct)}`, tone: 'brand' },
                            ].map((s) => (
                                <div key={s.l} className="p-5 border-r border-b border-neutral-200">
                                    <div className="overline">{s.l}</div>
                                    <div className={`font-mono-data text-[22px] tabular mt-2 ${s.tone === 'up' ? 'text-[#0B8A3E]' : s.tone === 'down' ? 'text-[#C3341C]' : s.tone === 'brand' ? 'text-[#2F6B47]' : ''}`}>
                                        {s.v}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <button
                                data-testid={TID.detail.insightBtn}
                                onClick={() => insightMut.mutate()}
                                disabled={insightMut.isPending}
                                className="inline-flex items-center gap-2 border border-brand-navy bg-brand-navy text-white px-5 py-3 text-[13.5px] hover-invert hover:bg-brand-gold hover:text-brand-navy"
                               
                            >
                                <Sparkles className="w-4 h-4" />
                                {insightMut.isPending ? 'Analysing…' : 'Get analyst take'}
                            </button>
                            <button
                                data-testid={TID.detail.favBtn}
                                onClick={() => user ? favMut.mutate() : toast.error('Sign in to save favorites')}
                                className="inline-flex items-center gap-2 border border-neutral-300 px-5 py-3 text-[13.5px] hover:border-brand-navy"
                               
                            >
                                <Bookmark className="w-4 h-4" /> Save
                            </button>
                            <Link
                                data-testid={TID.detail.compareBtn}
                                to={`/compare?slugs=${n.slug}`}
                                className="inline-flex items-center gap-2 border border-neutral-300 px-5 py-3 text-[13.5px] hover:border-brand-navy"
                               
                            >
                                Add to compare <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link
                                data-testid="detail-report-cta"
                                to={`/neighbourhoods/${n.slug}/report`}
                                className="inline-flex items-center gap-2 bg-brand-navy text-white px-5 py-3 text-[13.5px] hover:bg-neutral-800"
                               
                            >
                                Get the Intelligence Report <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>

                        {insightMut.data?.insight && (
                            <div data-testid={TID.detail.insightText} className="mt-8 border-l-2 border-[#2F6B47] pl-6 py-2">
                                <div className="overline text-[#2F6B47]">Analyst take</div>
                                <div className="font-editorial text-[18px] mt-3 leading-[1.55] whitespace-pre-line text-neutral-800">
                                    {insightMut.data.insight}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-5 order-first lg:order-none">
                        <div className="h-full min-h-[400px] bg-neutral-100">
                            <img src={n.hero_image} alt={n.name} className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Scores + Chart */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <div className="overline">Neighbourhood scorecard</div>
                        <div className="mt-6 space-y-4">
                            {Object.entries(n.metrics).map(([k, v]) => (
                                <MetricBar key={k} label={k.replace(/_/g, ' ')} value={v} tone={k === 'walkability' || k === 'schools' ? 'brand' : 'neutral'} />
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="overline">Median price · 6-year history</div>
                        <div className="mt-6 border border-neutral-200 p-6">
                            <ResponsiveContainer width="100%" height={280}>
                                <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: 10 }}>
                                    <XAxis dataKey="year" stroke="#a3a3a3" tick={{ fontFamily: 'JetBrains Mono', fontSize: 11 }} axisLine={{ stroke: '#e5e5e5' }} tickLine={false} />
                                    <YAxis stroke="#a3a3a3" tick={{ fontFamily: 'JetBrains Mono', fontSize: 11 }} axisLine={{ stroke: '#e5e5e5' }} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={60} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: 10, border: '1px solid #E8E2D5', fontFamily: 'JetBrains Mono', fontSize: 12, boxShadow: '0 4px 12px rgba(11,31,54,0.07)' }}
                                        formatter={(v) => cfmt(v)}
                                    />
                                    <Line type="linear" dataKey="price" stroke="#2F6B47" strokeWidth={1.5} dot={{ r: 3, fill: '#2F6B47' }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mt-3 text-[11.5px] text-neutral-500 leading-relaxed">
                            Curated market estimates, reviewed June 2026. Directional research — not an appraisal
                            or a guarantee of value. Verify current pricing with a local professional before acting.
                        </div>
                        <div className="mt-6 grid grid-cols-3 gap-6">
                            <div>
                                <div className="overline">Median rent 2BR</div>
                                <div className="font-mono-data text-[18px] tabular mt-2">{cfmt(n.median_rent_2br)}</div>
                            </div>
                            <div>
                                <div className="overline">Downtown commute</div>
                                <div className="font-mono-data text-[18px] tabular mt-2">{n.commute_downtown_min} min</div>
                            </div>
                            <div>
                                <div className="overline">Median age</div>
                                <div className="font-mono-data text-[18px] tabular mt-2">{n.demographics.median_age}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Demographics + Highlights */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <div className="overline">Who lives here</div>
                        <div className="mt-6 divide-y divide-neutral-200 border-y border-neutral-200">
                            {[
                                ['Family households', `${n.demographics.family_pct}%`],
                                ['Owner-occupied', `${n.demographics.owner_pct}%`],
                                ['Average household income', cfmt(n.demographics.avg_income)],
                                ['Median age', `${n.demographics.median_age} years`],
                            ].map(([l, v]) => (
                                <div key={l} className="flex items-center justify-between py-4">
                                    <div className="text-[14px] text-neutral-600">{l}</div>
                                    <div className="font-mono-data text-[15px] tabular">{v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="overline">Local highlights</div>
                        <ul className="mt-6 space-y-3">
                            {n.highlights?.map((h) => (
                                <li key={h} className="flex items-start gap-3">
                                    <span className="w-2 h-2 mt-2" style={{ background: '#2F6B47' }} />
                                    <span className="font-editorial text-[19px] tracking-editorial">{h}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8 flex flex-wrap gap-2">
                            {n.tags?.map((t) => (
                                <span key={t} className="text-[11px] uppercase tracking-[0.16em] px-2.5 py-1 border border-neutral-300">{t}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Suitability */}
            {n.suitability && (
                <section className="border-b border-neutral-200 bg-[#F7F7F5]">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
                        <div className="overline">Who this neighbourhood suits</div>
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-neutral-200">
                            {[
                                { l: 'Family', v: n.suitability.family },
                                { l: 'Professional', v: n.suitability.professional },
                                { l: 'Investor', v: n.suitability.investor },
                                { l: 'Retiree', v: n.suitability.retiree },
                            ].map((s) => (
                                <div key={s.l} className="border-r border-b border-neutral-200 p-6 bg-white">
                                    <div className="overline">{s.l}</div>
                                    <div className="font-mono-data text-[28px] tabular mt-2 text-[#2F6B47]">{s.v}</div>
                                    <div className="mt-3 h-[4px] bg-neutral-100">
                                        <div className="h-full" style={{ width: `${s.v}%`, background: '#2F6B47' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                        {n.typical_buyer && (
                            <div className="mt-8 border-l-2 border-[#2F6B47] pl-5">
                                <div className="overline text-[#2F6B47]">Typical buyer</div>
                                <p className="font-editorial text-[19px] italic text-neutral-800 mt-2 leading-relaxed max-w-3xl">{n.typical_buyer}</p>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Schools & Hospitals & Transit */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-6">
                        <div className="overline">Schools in the catchment</div>
                        <div className="mt-6 divide-y divide-neutral-200 border-y border-neutral-200">
                            {(n.schools_named || []).map((s) => (
                                <div key={s.name} className="flex items-center justify-between py-4">
                                    <div>
                                        <div className="font-editorial text-[18px] tracking-editorial">{s.name}</div>
                                        <div className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 mt-0.5">{s.type}</div>
                                    </div>
                                    <div className="font-mono-data text-[18px] tabular text-[#2F6B47]">{s.rating?.toFixed(1)}</div>
                                </div>
                            ))}
                            {!n.schools_named?.length && (
                                <div className="py-6 text-neutral-500 text-[14px]">School data not yet catalogued for this area.</div>
                            )}
                        </div>
                    </div>
                    <div className="lg:col-span-6 space-y-8">
                        {n.transit_notes && (
                            <div>
                                <div className="overline">Transit & commute</div>
                                <p className="mt-4 text-[15px] leading-relaxed text-neutral-800 font-editorial">{n.transit_notes}</p>
                            </div>
                        )}
                        {n.hospitals?.length > 0 && (
                            <div>
                                <div className="overline">Hospitals & health</div>
                                <ul className="mt-4 space-y-2">
                                    {n.hospitals.map((h) => (
                                        <li key={h} className="text-[15px] text-neutral-800">
                                            <span className="inline-block w-1.5 h-1.5 bg-[#D9381E] mr-2 align-middle" />
                                            {h}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Pros / Cons */}
            {(n.pros?.length || n.cons?.length) && (
                <section className="border-b border-neutral-200">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-7">
                            <div className="overline text-[#0B8A3E]">Reasons to buy here</div>
                            <ul className="mt-5 space-y-4">
                                {n.pros?.map((p) => (
                                    <li key={p} className="flex items-start gap-3 text-[16px] leading-relaxed">
                                        <span className="font-mono-data text-[13px] text-[#0B8A3E] mt-1">+</span>
                                        <span className="font-editorial tracking-editorial">{p}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="overline text-[#C3341C]">Reasons to think twice</div>
                            <ul className="mt-5 space-y-4">
                                {n.cons?.map((c) => (
                                    <li key={c} className="flex items-start gap-3 text-[16px] leading-relaxed">
                                        <span className="font-mono-data text-[13px] text-[#C3341C] mt-1">−</span>
                                        <span className="font-editorial tracking-editorial">{c}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            )}

            {/* Notable streets & landmarks */}
            {(n.notable_streets?.length || n.local_landmarks?.length) && (
                <section className="border-b border-neutral-200">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-2 gap-10">
                        {n.notable_streets?.length > 0 && (
                            <div>
                                <div className="overline">Streets worth knowing</div>
                                <ul className="mt-5 space-y-2">
                                    {n.notable_streets.map((s) => (
                                        <li key={s} className="font-editorial text-[19px] tracking-editorial">{s}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {n.local_landmarks?.length > 0 && (
                            <div>
                                <div className="overline">Local landmarks</div>
                                <ul className="mt-5 space-y-2">
                                    {n.local_landmarks.map((s) => (
                                        <li key={s} className="font-editorial text-[19px] tracking-editorial">{s}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Map */}
            {n.coords?.lat && (
                <section className="border-b border-neutral-200 bg-[#F7F7F5]">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
                        <div className="flex items-baseline justify-between mb-4 gap-4">
                            <div className="overline">On the map · with school boundaries</div>
                            {boundaries?.boards?.length > 0 && (
                                <div className="text-[11px] font-mono-data text-neutral-500 hidden sm:block" data-testid="map-boards-summary">
                                    {boundaries.boards.map((b) => b.board_slug.toUpperCase()).join(' · ')}
                                    {boundaries.has_per_school ? ' · authoritative catchments loaded' : ' · board-level only'}
                                </div>
                            )}
                        </div>
                        <MapView singlePoint={n} height={460} center={[n.coords.lat, n.coords.lng]} zoom={13} showCatchment boundaries={boundaries} />
                        {boundaries?.schools?.length > 0 && (
                            <div className="mt-6 border border-neutral-200 bg-white p-5 lg:p-6" data-testid="map-authoritative-schools">
                                <div className="overline">Authoritative catchments · this exact address</div>
                                <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                                    {boundaries.schools.map((s, i) => (
                                        <li key={i} className="flex items-baseline justify-between gap-3 border-b border-dashed border-neutral-200 py-2 last:border-none">
                                            <div className="min-w-0">
                                                <div className="font-editorial text-[15px] leading-tight truncate">{s.school_name}</div>
                                                <div className="text-[11px] font-mono-data text-neutral-500 uppercase tracking-wider mt-0.5">
                                                    {s.boundary_type || 'Catchment'} · {s.board_slug?.toUpperCase()}
                                                </div>
                                            </div>
                                            {s.kind === 'WalkZone' && (
                                                <span className="text-[10px] font-mono-data uppercase tracking-widest text-[#B87A2C] shrink-0">Walk-zone</span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-4 text-[11.5px] text-neutral-500 leading-relaxed">
                                    Data sources: HWDSB, HCDSB and HDSB feature servers via ArcGIS REST. Board polygons from OpenNorth Represent. Boundaries change annually — verify with the board before making school-based decisions.
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Watchlist alert */}
            <WatchlistPanel neighbourhood={n} />
        </div>
    );
}
